import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { map, Observable, startWith, Subject, takeUntil } from 'rxjs';

import { CompaniesList } from '@shure/cloud/shared/switch-organization/data-access';
import { noWhitespaceValidator } from '@shure/cloud/shared/ui/components';
import { PrismDialogRef, PrismSnackbarService } from '@shure/prism-angular-components';

import { SwitchOrganizationService } from '../../services/switch-organization.service';

@Component({
	selector: 'sh-create-organization-dialog',
	templateUrl: './create-organization-dialog.component.html',
	styleUrls: ['./create-organization-dialog.component.scss']
})
export class CreateOrganizationDialogComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();
	private myCompaniesList!: CompaniesList[];
	public formGroup!: FormGroup;
	public isVerified = false;
	public showCompanyList = false;
	public selectedCompanyOption!: CompaniesList | null;
	public filteredOptions!: Observable<CompaniesList[]>;
	public filteredCompanyList: CompaniesList[] = [];
	public selectedCompanyOptionName!: string;
	constructor(
		public formBuilder: FormBuilder,
		private dialogRef: PrismDialogRef<CreateOrganizationDialogComponent>,
		public switchOrganizationService: SwitchOrganizationService,
		private snackBarService: PrismSnackbarService,
		private translocoService: TranslocoService
	) {}

	public ngOnInit(): void {
		this.getCompanies();
		this.buildFormGroup();
	}

	public getCompanies(): void {
		this.switchOrganizationService
			.getMyCompanies()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (companiesListResp) => {
					this.myCompaniesList = companiesListResp.body?.body?.items || [];
					this.setupAutocomplete();
				},
				error: (error) => {
					const errorMessage = error.error;
					this.snackBarService.open({
						message: this.translocoService.translate(
							errorMessage?.i18nKey
								? 'cloud-licensing-portal.messages.' + errorMessage.i18nKey
								: errorMessage.message
						),
						actionText: this.translocoService.translate('common.snackbar.ok')
					});
				}
			});
	}

	public buildFormGroup(): void {
		this.formGroup = this.formBuilder.group({
			organizationName: new FormControl('', [
				Validators.required,
				noWhitespaceValidator(),
				Validators.maxLength(150)
			]),
			companyName: new FormControl('', [noWhitespaceValidator()])
			// The below code can be uncommented when we support the verified organizations feature
			// isVerified: new FormControl(false, [Validators.requiredTrue])
		});
	}

	/**
	 * Sets up the autocomplete stream for the 'companyName' form control.
	 * Listens for user input changes and filters the company list accordingly.
	 * The filtered results are exposed via `filteredOptions` for display in the UI.
	 */
	public setupAutocomplete(): void {
		this.formGroup
			.get('companyName')!
			.valueChanges.pipe(
				startWith(''),
				map((value) => this.filterCompanyList(value || ''))
			)
			.subscribe((filtered) => {
				this.filteredCompanyList = filtered;
			});
	}

	/**
	 * Filters `myCompaniesList` based on the typed input.
	 * Matches company names case-insensitively and updates `selectedCompanyOption`
	 * if there's an exact match. Returns a list of all matching companies.
	 *
	 * @param value - The input string to filter against the company list.
	 * @returns Filtered array of `CompaniesList` items.
	 */
	public filterCompanyList(value: string): CompaniesList[] {
		const input = value.toLowerCase().trim();
		const matchedCompany = this.myCompaniesList.find((c) => c.companyName?.toLowerCase() === input);
		this.selectedCompanyOption = matchedCompany || null;
		return this.myCompaniesList.filter((c) => c.companyName?.toLowerCase().includes(input));
	}

	public onCompanyInput(input: string): void {
		this.showCompanyList = true;
		this.formGroup.patchValue({ companyName: input });
	}

	public onCompanySelected(option: CompaniesList): void {
		this.formGroup.patchValue({ companyName: option.companyName });
		this.selectedCompanyOption = option;
		this.showCompanyList = false;
	}

	public get isCompanyVerified(): boolean {
		const selectedName = this.selectedCompanyOption?.companyName?.toLowerCase();
		const inputValue = this.formGroup.get('companyName')?.value?.toLowerCase();
		return !!this.selectedCompanyOption?.isVerified && selectedName === inputValue;
	}

	public createOrganization(): void {
		if (this.formGroup.valid) {
			let companyId;
			const companyName = this.formGroup.value.companyName.trim();

			if (
				this.selectedCompanyOption?.isVerified &&
				this.selectedCompanyOption?.companyId &&
				this.selectedCompanyOption?.companyName?.toLowerCase() === companyName.toLowerCase()
			) {
				companyId = this.selectedCompanyOption.companyId;
			}

			const isVerified = !!companyId;

			const createOrganizationData = {
				companyName,
				companyId,
				organizationName: this.formGroup.value.organizationName.trim(),
				isVerified
			};
			this.switchOrganizationService.createOrganization(createOrganizationData);
			this.dialogRef.close();
		}
	}

	public closeDialog(): void {
		this.dialogRef.close();
	}

	/**
	 * Lifecycle hook that is called when the component is being destroyed.
	 * It unsubscribes from observables and cleans up any resources.
	 */
	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Returns the appropriate validation error message for the 'organizationName' field
	 * based on the current validation state. It checks for 'required', 'whitespace',
	 * and 'maxlength' errors, and returns a translated string for each.
	 * If no errors are present or the field hasn't been touched, returns null.
	 */
	public getOrganizationNameError(): string | null {
		const control = this.formGroup.get('organizationName');
		if (control?.touched) {
			if (control.hasError('required') || control.hasError('whitespace')) {
				return this.translocoService.translate('cloud.shared.error-labels.organization-name-required');
			}
			if (control.hasError('maxlength')) {
				return this.translocoService.translate('cloud.shared.error-labels.must-not-be-greater-than-length', {
					label: this.translocoService.translate('cloud.shure-cloud.org-settings.organization-name'),
					number: 150
				});
			}
		}
		return null;
	}
}
